import { useMutation } from "urql";
import {
  FragmentType,
  getFragmentData,
  graphql,
} from "../../../../../__generated__/gql";
import { useCallback, useEffect, useRef } from "react";
import ConfirmationModal from "../../../../../ui/Modal/ConfirmationModal";
import { useToast } from "../../../../../ui/Toast/ToastContext";
import { z } from "zod";
import { Controller, useForm } from "react-hook-form";
import TextInput from "../../../../../ui/TextInput/TextInput";
import Label from "../../../../../ui/Label/Label";
import { zodResolver } from "@hookform/resolvers/zod";
import Feedback from "../../../../../ui/Feedback/Feedback";
import TagSelect from "../../../../../components/TagSelect/TagSelect";
import Textarea from "../../../../../ui/Textarea/Textarea";
import styles from "./Details.module.scss";
import {
  ArticleStatus,
  GenerationStep,
} from "../../../../../__generated__/gql/graphql";
import GeneratingBanner from "./GeneratingBanner";

const EditArticleDetailsFragment = graphql(/* GraphQL */ `
  fragment EditArticleDetails on Article {
    id
    version
    title
    status
    processingUntilStep
    keyword
    tag {
      id
      name
    }
    metaDescription
    excerpt
    additionalPrompt
  }
`);

const EditArticleDetailsMutation = graphql(/* GraphQL */ `
  mutation editArticleDetails($input: UpdateArticleInput!) {
    updateArticle(input: $input) {
      ...EditArticleDetails
    }
  }
`);

const schema = z.object({
  title: z.string().min(1, "Required"),
  keyword: z.string().min(1, "Required"),
  tag: z.string().optional(),
  metaDescription: z.string().optional(),
  excerpt: z.string().optional(),
  additionalPrompt: z.string().optional(),
});

export type DetailFormData = z.infer<typeof schema>;

function Details({
  onClose,
  onChange,
  id,
  isOpen,
  data,
}: {
  onClose: () => void;
  onChange?: (isDirty: boolean) => void;
  id: string;
  isOpen: boolean;
  data: FragmentType<typeof EditArticleDetailsFragment>;
}) {
  const { showToast } = useToast();
  const [, editArticleDetails] = useMutation(EditArticleDetailsMutation);
  const article = getFragmentData(EditArticleDetailsFragment, data);
  const previousArticleStatus = useRef(article.status);

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting, isDirty },
  } = useForm<DetailFormData>({
    defaultValues: {
      title: article.title ?? "",
      keyword: article.keyword,
      tag: article.tag?.id,
      metaDescription: article.metaDescription ?? undefined,
      excerpt: article.excerpt ?? undefined,
      additionalPrompt: article.additionalPrompt ?? undefined,
    },
    resolver: zodResolver(schema),
  });

  const processing = article.status === ArticleStatus.Processing;
  const generating =
    processing &&
    (!article.processingUntilStep ||
      GenerationStep.Outline === article.processingUntilStep);
  const regenerating = article.version > 1;

  useEffect(() => {
    if (previousArticleStatus.current === ArticleStatus.Processing) {
      reset({
        title: article.title ?? "",
        keyword: article.keyword,
        tag: article.tag?.id,
        metaDescription: article.metaDescription ?? undefined,
        excerpt: article.excerpt ?? undefined,
        additionalPrompt: article.additionalPrompt ?? undefined,
      });
    }

    previousArticleStatus.current = article.status;
  }, [article, reset]);

  const onSubmit = useCallback(
    async (data: DetailFormData) => {
      const response = await editArticleDetails({
        input: {
          id,
          title: data.title,
          keyword: data.keyword,
          tag: data.tag,
          metaDescription: data.metaDescription,
          excerpt: data.excerpt,
          additionalPrompt: data.additionalPrompt,
        },
      });

      const article = getFragmentData(
        EditArticleDetailsFragment,
        response.data?.updateArticle,
      );

      if (article) {
        reset({
          title: article.title ?? "",
          keyword: article.keyword,
          tag: article.tag?.id,
          metaDescription: article.metaDescription ?? undefined,
          excerpt: article.excerpt ?? undefined,
          additionalPrompt: article.additionalPrompt ?? undefined,
        });

        showToast({ title: "Article updated" });
      }
    },
    [editArticleDetails, id, reset, showToast],
  );

  useEffect(() => {
    if (onChange) {
      onChange(isDirty);
    }
  }, [isDirty, onChange]);

  return (
    <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
      <ConfirmationModal
        sticky
        className={styles.formFooter}
        loading={isSubmitting || !isOpen}
        onClose={onClose}
        confirmationText="Save"
        confirmDisabled={processing}
      >
        {processing && <GeneratingBanner id={article.id} />}
        <Label label="Title">
          <Controller
            control={control}
            name="title"
            render={({ field, fieldState: { error } }) => (
              <div>
                <TextInput
                  {...field}
                  disabled={processing}
                  loading={processing && !field.value}
                  loadingMessage={regenerating ? "Regenerating" : "Generating"}
                />
                {error?.message && <Feedback>{error.message}</Feedback>}
              </div>
            )}
          />
        </Label>
        <Label label="Keyword">
          <Controller
            control={control}
            name="keyword"
            render={({ field, fieldState: { error } }) => (
              <div>
                <TextInput {...field} disabled={processing} />
                {error?.message && <Feedback>{error.message}</Feedback>}
              </div>
            )}
          />
        </Label>
        <Label label="Tag" htmlFor="tag-select">
          <Controller
            control={control}
            name="tag"
            render={({ field, fieldState: { error } }) => (
              <div>
                <TagSelect
                  {...field}
                  disabled={processing}
                  loading={generating && !field.value}
                  loadingMessage={regenerating ? "Regenerating" : "Generating"}
                />
                {error?.message && <Feedback>{error.message}</Feedback>}
              </div>
            )}
          />
        </Label>
        <Label label="Meta description">
          <Controller
            control={control}
            name="metaDescription"
            render={({ field, fieldState: { error } }) => (
              <div>
                <Textarea
                  {...field}
                  autosize
                  minRows={3}
                  maxRows={5}
                  disabled={processing}
                  loading={generating && !field.value}
                  loadingMessage={regenerating ? "Regenerating" : "Generating"}
                />
                {error?.message && <Feedback>{error.message}</Feedback>}
              </div>
            )}
          />
        </Label>
        <Label label="Excerpt">
          <Controller
            control={control}
            name="excerpt"
            render={({ field, fieldState: { error } }) => (
              <div>
                <Textarea
                  {...field}
                  autosize
                  minRows={3}
                  maxRows={5}
                  disabled={processing}
                  loading={generating && !field.value}
                  loadingMessage={regenerating ? "Regenerating" : "Generating"}
                />
                {error?.message && <Feedback>{error.message}</Feedback>}
              </div>
            )}
          />
        </Label>
        <Label label="Custom prompt">
          <Controller
            control={control}
            name="additionalPrompt"
            render={({ field, fieldState: { error } }) => (
              <div>
                <Textarea
                  {...field}
                  autosize
                  minRows={3}
                  maxRows={5}
                  disabled={processing}
                  fullScreenLabel="Custom prompt"
                  allowFullScreen
                />
                {error?.message && <Feedback>{error.message}</Feedback>}
              </div>
            )}
          />
        </Label>
      </ConfirmationModal>
    </form>
  );
}

export default Details;
